/**
 * automationApi — el estado de la automatización comercial de un lead.
 *
 * Fino a propósito: el backend ya devuelve el modelo de lectura completo
 * (`automation_engine.status_for_lead`), así que aquí no se calcula nada ni se
 * guarda nada. Una copia en el navegador de "por dónde va la secuencia" sería
 * la segunda verdad sobre un hecho que solo conoce el motor.
 */
import { api } from "@/lib/api";

/** Estado de la secuencia, o `null` si este lead nunca se inscribió. */
export async function getLeadAutomation(leadId) {
  const { data } = await api.get(
    `/leads/${encodeURIComponent(leadId)}/automation`,
  );
  return {
    enabled: Boolean(data?.enabled),
    sequence: data?.sequence || null,
    // Por qué este lead no está en ninguna secuencia. Solo viene cuando no
    // hay secuencia: un lead al que nunca se le escribe sin decir por qué es
    // indistinguible de un motor roto.
    blockedReason: data?.blocked_reason || null,
    // Los DOS ejes del permiso, por separado y con sus fechas. No se derivan
    // el uno del otro: "no se ha dado de baja" nunca significa "aceptó".
    permission: data?.permission || null,
  };
}

/**
 * Excluir de la automatización, o volver a permitirla.
 *
 * Devuelve el permiso ya actualizado. `allow` solo puede fallar con 409 si el
 * lead ya no existe: levantar la baja dejó de exigir un consentimiento
 * previo, porque ése era el mismo gate por otro camino — y dejaba atrapado a
 * quien pidió la baja por error y lo decía.
 */
export async function controlLeadPermission(leadId, action) {
  const { data } = await api.post(
    `/leads/${encodeURIComponent(leadId)}/automation/${encodeURIComponent(action)}`,
  );
  return { permission: data?.permission || null, sequence: data?.sequence || null };
}

/**
 * Qué se puede hacer hoy con el permiso de este lead.
 *
 * Una baja siempre se puede levantar. Antes se ocultaba el botón cuando no
 * constaba consentimiento —el motor lo habría bloqueado igual un paso más
 * tarde— y eso dejaba de ser cierto al retirar ese gate: hoy el único
 * bloqueo es la baja, así que levantarla sí hace algo.
 */
export function permissionActionFor(permission) {
  if (!permission) return null;
  return permission.opted_out ? "allow" : "opt_out";
}

/**
 * Pausar, reanudar o cancelar. Devuelve el estado ya actualizado, así que el
 * llamante no tiene que volver a preguntar.
 *
 * `cancel` es definitivo: lo que para una persona el motor no lo reanuda solo.
 */
export async function controlLeadAutomation(leadId, action) {
  const { data } = await api.post(
    `/leads/${encodeURIComponent(leadId)}/automation/${encodeURIComponent(action)}`,
  );
  return data?.sequence || null;
}

// ---------------------------------------------------------------------------
// Vocabulario, en el idioma de quien vende
// ---------------------------------------------------------------------------

export const AUTOMATION_STATUS_LABEL = {
  scheduled: "Programada",
  active: "En curso",
  paused: "En pausa",
  completed: "Terminada",
  cancelled: "Cancelada",
  failed: "Detenida",
};

export const AUTOMATION_STATUS_TONE = {
  scheduled: "blue",
  active: "emerald",
  paused: "amber",
  completed: "stone",
  cancelled: "stone",
  failed: "red",
};

/**
 * Por qué se paró, contado a una persona.
 *
 * La distinción que importa está en `cancel_kind`, no en el texto: que el lead
 * contestara es el sistema funcionando; que fallara una plantilla es trabajo
 * para alguien. Un motivo desconocido se enseña tal cual antes que inventarse
 * una explicación.
 */
/**
 * Texto del registro de consentimiento. Es DATO, no permiso: dice qué se le
 * enseñó a este lead, no si se le puede escribir.
 */
export const CONSENT_LABEL = {
  granted: "Aceptó recibir WhatsApp",
  missing: "Sin consentimiento registrado",
};

export const OPT_OUT_LABEL = {
  manual: "Excluido por el equipo",
  whatsapp_inbound: "Pidió la baja por WhatsApp",
  unknown: "Dado de baja",
};

export function optOutLabel(permission) {
  if (!permission?.opted_out) return null;
  return OPT_OUT_LABEL[permission.opted_out_source] || OPT_OUT_LABEL.unknown;
}

const REASON_TEXT = {
  inbound_reply: "El lead respondió, así que se detuvo sola.",
  human_contact: "Alguien del equipo ya escribió al lead.",
  manual: "La detuvo una persona del equipo.",
  lead_archived: "El lead está archivado.",
  lead_converted: "El lead ya es inversor.",
  lead_stage_not_eligible: "El lead ya no está en la fase inicial.",
  no_valid_phone: "El lead no tiene un teléfono utilizable.",
  // HISTÓRICO: ningún motivo nuevo puede ser éste. El consentimiento previo
  // dejó de bloquear, pero hay secuencias y decisiones de antes que lo
  // llevan escrito, y una pantalla que no sabe traducir su propio pasado
  // enseña un código en crudo donde había una explicación.
  no_whatsapp_consent:
    "Se detuvo porque no constaba consentimiento de WhatsApp, una regla que "
    + "ya no se aplica.",
  opted_out: "El lead pidió no recibir mensajes automáticos.",
  consent_required:
    "Llegó por un formulario de Meta sin permiso: no marcó la casilla de WhatsApp y su formulario no da el permiso al enviarlo. Nada automático sin su permiso.",
  identity_review_pending:
    "Podría ser la misma persona que otra ficha: nada automático hasta que alguien lo revise.",
  step_expired: "El paso se quedó demasiado atrás y caducó.",
  lead_missing: "El lead ya no existe.",
  template_not_configured: "No hay plantilla configurada para el paso.",
  no_usable_name: "El nombre del lead no sirve para saludar.",
  template_variables_unsupported:
    "La plantilla pide más variables de las que la automatización rellena.",
  invalid_timezone:
    "La zona horaria configurada no existe, así que no se puede saber si es "
    + "hora de escribir. No se envía nada hasta corregirla.",
  send_key_consumed_after_failure:
    "El envío falló y su clave ya está gastada: este paso no se puede repetir.",
};

export function automationReasonText(reason) {
  if (!reason) return null;
  const raw = String(reason);
  for (const [key, text] of Object.entries(REASON_TEXT)) {
    if (raw.startsWith(key)) return text;
  }
  if (raw.startsWith("template_unavailable")) {
    return "La plantilla aprobada no está disponible en Meta.";
  }
  if (raw.startsWith("provider_rejected")) {
    return "El proveedor rechazó el envío.";
  }
  if (raw.startsWith("provider_error") || raw.startsWith("catalog_error")) {
    return "El proveedor falló y se agotaron los reintentos.";
  }
  return raw;
}

/** Qué botones tienen sentido con este estado. Nada que no vaya a funcionar. */
export function automationControlsFor(status) {
  if (status === "scheduled" || status === "active") {
    return ["pause", "cancel"];
  }
  if (status === "paused") return ["resume", "cancel"];
  return [];
}
